// Case-study content for /work/:id. Screenshots live in public/projects/<id>/.
const shots = (id, desktop, mobile = 0) => ({
  desktop: Array.from({ length: desktop }, (_, i) => `${process.env.PUBLIC_URL}/projects/${id}/desktop-${i + 1}.webp`),
  mobile: Array.from({ length: mobile }, (_, i) => `${process.env.PUBLIC_URL}/projects/${id}/mobile-${i + 1}.webp`)
});

export const projects = [
  {
    id: "bena",
    title: "Bena Line",
    category: "Ecommerce",
    link: "https://www.bena.sa.com/",
    context: "Client project",
    role: "Full-Stack Developer",
    sector: "Construction · B2B trade",
    market: "Saudi Arabia",
    summary:
      "B2B marketplace connecting construction buyers with suppliers, contractors and service providers — search, quotations and project bids in one place.",
    tagline: "A B2B marketplace bringing construction-materials trade in Saudi Arabia online.",
    overview: [
      "BENA Line is a digital trading platform for the Saudi construction market, built in line with Saudi Vision 2030. It connects buyers with suppliers, contractors and service providers, replacing phone-and-spreadsheet sourcing with a searchable catalogue, online quotation requests and project bidding."
    ],
    brief:
      "Construction procurement is fragmented: thousands of products, many suppliers, and prices negotiated request by request. The platform had to make a large, constantly updated product database easy to search, serve two very different audiences — buyers and sellers — and turn browsing into quotation requests.",
    approach:
      "I built the front end as a fast React single-page app on a Tailwind design system, organised around a tabbed search hub — Products, Services and Project Bids — filtered by city, category, sub-category and brand. Buyers and sellers each get a dedicated journey, while the catalogue, supplier directory and FAQ are reusable carousel-driven modules backed by a NestJS API.",
    features: [
      { title: "Unified search hub", text: "Tabbed search across products, services and project bids, filtered by city, category, sub-category, brand and product name." },
      { title: "Buyer & seller journeys", text: "Separate onboarding paths for buyers sourcing materials and suppliers listing their catalogues." },
      { title: "Quotation workflow", text: "Request-a-quotation and add-to-list actions on every product, feeding a single Get Quotation flow." },
      { title: "Project bids", text: "Contractors and service providers can discover and respond to construction project opportunities." },
      { title: "Supplier directory", text: "A browsable showcase of supplier brands leading into each supplier's catalogue." },
      { title: "VAT toggle & accounts", text: "Prices with or without VAT, sign-in and sign-up, and a language switcher in the top bar." }
    ],
    facts: [
      { value: "10K+", label: "Quotation requests" },
      { value: "30+", label: "Suppliers" },
      { value: "30+", label: "Contractors" },
      { value: "10+", label: "Completed projects" }
    ],
    factsNote: "Platform figures as published on bena.sa.com.",
    stack: [
      { name: "React.js", use: "Single-page app, routing and UI state" },
      { name: "Tailwind CSS", use: "Design system and responsive layouts" },
      { name: "NestJS", use: "REST API for catalogue, quotations and bids" },
      { name: "Swiper", use: "Product and supplier carousels" },
      { name: "Vite", use: "Build tooling and code-splitting" }
    ],
    mobileNote: "On phones the search hub stacks into a single-column form, and every carousel stays swipeable and thumb-friendly.",
    gallery: shots("bena", 3, 3)
  },
  {
    id: "filesdna",
    title: "FilesDNA",
    category: "SaaS",
    link: "https://filesdna.netlify.app/",
    context: "Borderless Security",
    role: "Full-Stack Developer · Product team",
    sector: "GovTech · Enterprise ERP",
    market: "UK · UAE · Iraq",
    summary:
      "A 60-module ERP for governments and enterprises — I contributed to building and planning the product, and built its public site.",
    tagline: "An ERP for governments and enterprises where every record carries verifiable DNA — a product I helped develop and plan.",
    overview: [
      "FilesDNA is Borderless Security's ERP for governments and enterprises: more than sixty modules — from document management and e-signatures to payroll, procurement and permits — on one data model. Every document, signature and transaction is fingerprinted, so the platform can prove who did what, and when.",
      "I'm part of the team behind it, and I also built its public site — which has to explain a complex, security-heavy platform to ministries and enterprise buyers, in English and Arabic."
    ],
    contribution:
      "Beyond the website, I worked on FilesDNA itself as one of its contributors — helping develop the platform and taking part in planning how it grows. That work sits alongside my role at Borderless Security integrating document workflows, e-signature and blockchain anchoring, so I know the product from the inside, not just from its marketing.",
    brief:
      "Sell trust, not features. The audience is procurement committees and IT leads who need to understand tamper-evidence, signature provenance and data residency without reading a whitepaper — and a 58-module catalogue had to feel approachable rather than overwhelming.",
    approach:
      "For the site, I built it in Next.js as a story that moves from problem to proof. “Twelve systems will always disagree” contrasts a twelve-vendor stack with one platform; interactive sections then let visitors experience the guarantees for themselves. Technical depth lives on dedicated pages — features, technical features, use cases and case studies — with a full Arabic version and light and dark themes.",
    features: [
      { title: "Live Document DNA demo", text: "Drop in any file to see its SHA-256 fingerprint rendered as a DNA strand — then change a byte and watch verification fail." },
      { title: "Searchable module library", text: "58 modules across five families, filterable by family and searchable by name, each with its own detail." },
      { title: "Stack-vs-platform comparison", text: "Visualises 12 vendors, 12 logins, 12 audit logs and 66 integrations collapsing into one platform." },
      { title: "Signature provenance", text: "Explains the root-of-trust chain — the root signs, the tenant issues — behind every e-signature." },
      { title: "Security & data residency", text: "Five protection layers, and cloud, on-premise or offline-edge deployment so data stays in-country." },
      { title: "Bilingual & themable", text: "English and Arabic versions, light and dark themes, and sector-specific use-case pages." }
    ],
    facts: [
      { value: "60+", label: "Modules on one data model" },
      { value: "3", label: "Deployment options" },
      { value: "~15s", label: "To provision a workspace" },
      { value: "2", label: "Languages — EN & AR" }
    ],
    factsNote: "Product figures as published on the FilesDNA site.",
    stack: [
      { name: "Next.js", use: "Routing, static generation and localised pages" },
      { name: "React", use: "Interactive demo, module library and comparisons" },
      { name: "SHA-256 hashing", use: "File fingerprinting behind the Document DNA demo" },
      { name: "Canvas animation", use: "Animated DNA helix in the hero" },
      { name: "Netlify", use: "Hosting and continuous deployment" }
    ],
    mobileNote: "The helix, comparison grid and module library all reflow for phones — the module search stays usable one-handed.",
    gallery: shots("filesdna", 3, 3)
  },
  {
    id: "borderless",
    title: "Borderless Security",
    category: "Corporate",
    link: "https://borderlesssecurity.netlify.app/",
    context: "Borderless Security",
    role: "Full-Stack Developer",
    sector: "Cyber security · Software",
    market: "UK · Global",
    summary:
      "Corporate site for a UK cyber-security firm — services, the DNA product suite and an interactive self-healing hero.",
    tagline: "Corporate site for a Manchester-headquartered cyber-security and software company.",
    overview: [
      "Borderless Security is a security and software company founded in the UK in 2013 and headquartered in Manchester. It delivers network security, penetration testing, AI and secure software, plus its own identity suite: FilesDNA, UserDNA and SignDNA.",
      "The site brings all of it under one brand, from boardroom-level positioning down to individual service and product pages."
    ],
    brief:
      "Project deep technical credibility to enterprise buyers while staying approachable. The site had to cover more than a dozen service lines and three products without reading like a catalogue — and make an abstract value, resilience, feel tangible.",
    approach:
      "I built it in Next.js around a dark, editorial design language and one memorable interaction: the hero's “Disrupt it. Watch it recover.” visual, whose integrity meter heals itself after a visitor disrupts it. Service cards show what the work actually looks like — a recon terminal, risk-ranked pentest findings, a neural-network diagram, a verification code sample.",
    features: [
      { title: "Self-healing hero", text: "An interactive particle visual with a live integrity meter that recovers after the visitor disrupts it." },
      { title: "Cards that show the work", text: "Terminal recon output, ranked pentest findings, an AI network diagram and a code sample — one per service." },
      { title: "The DNA suite", text: "FilesDNA, UserDNA and SignDNA presented as a single identity story, with highlights for each product." },
      { title: "Four-stage engagement", text: "Assess → Architect → Build → Test & Defend, each with concrete deliverables." },
      { title: "Proof at a glance", text: "Client logos alongside headline figures for projects, engineers, offices and enterprise clients." },
      { title: "Deep page structure", text: "Dedicated pages for every service and product, plus About, Clients and Insights." }
    ],
    facts: [
      { value: "1,440+", label: "Projects delivered" },
      { value: "360+", label: "Engineers & specialists" },
      { value: "14+", label: "Offices worldwide" },
      { value: "2013", label: "Founded in the UK" }
    ],
    factsNote: "Company figures as published on the Borderless Security site.",
    stack: [
      { name: "Next.js", use: "App structure, routing and Turbopack builds" },
      { name: "React", use: "Interactive hero, service cards and product sections" },
      { name: "Canvas animation", use: "The self-healing particle visual" },
      { name: "Netlify", use: "Hosting and continuous deployment" }
    ],
    mobileNote: "On phones the hero visual sits above the headline, stats fold into a two-column grid, and every service card keeps its live preview.",
    gallery: shots("borderless", 3, 3)
  },
  {
    id: "gopro",
    title: "Go Pro",
    category: "Ecommerce",
    link: "https://goo-pro.netlify.app/",
    context: "Client project",
    role: "Full-Stack Developer",
    sector: "Automotive · Car care",
    market: "Riyadh, Saudi Arabia",
    summary: "Car-care storefront for a Riyadh business — cinematic service showcases, an online store and call-back booking.",
    tagline: "E-commerce and booking site for a Riyadh car-care and car-parts business.",
    overview: [
      "Go Pro is a Riyadh-based one-stop shop for car parts and car care: nano-ceramic coating, window film, upholstery and paint-protection film. The site pairs a cinematic brand showcase with an online store and a lead-capture flow that turns visitors into booked appointments."
    ],
    brief:
      "Premium car care is a visual, trust-driven purchase. The site had to feel high-end, explain four distinct services clearly, and make requesting a call effortless — especially on phones.",
    approach:
      "I designed a dark automotive aesthetic around full-bleed vehicle photography and built it in React with Chakra UI. Each service gets its own scroll section with imagery and an Explore call to action. The booking form captures car model, preferred visit day and services of interest, and a floating WhatsApp button keeps help one tap away. The store runs on a NestJS API behind Nginx.",
    features: [
      { title: "Cinematic service showcase", text: "Full-bleed sections for nano-ceramic coating, window filming, upholstery and paint protection." },
      { title: "Online store", text: "A car-parts catalogue with cart, served by a NestJS API." },
      { title: "Call-back booking", text: "Captures contact details, car type and model, best visit day and the services the customer wants." },
      { title: "WhatsApp support", text: "A floating “Need help?” button for instant contact from any page." },
      { title: "Language switcher", text: "Header language selector for English and Arabic audiences." },
      { title: "Built for phones", text: "Stacked layouts, large tap targets and a slide-out menu on mobile." }
    ],
    stack: [
      { name: "React.js", use: "Storefront and booking UI" },
      { name: "Chakra UI", use: "Component system and responsive styling" },
      { name: "NestJS", use: "Store and booking API" },
      { name: "Nginx", use: "Reverse proxy and static serving" }
    ],
    mobileNote: "Each service becomes a full-screen card on phones, with the booking call to action always within thumb reach.",
    gallery: shots("gopro", 3, 3)
  },
  {
    id: "us-incorporated",
    title: "US Incorporated",
    category: "Corporate",
    link: "https://www.us-incorporated.com/",
    context: "Pancode",
    role: "Frontend Developer",
    sector: "Manufacturing · Distribution",
    market: "Six countries",
    summary:
      "Multi-brand group site for a manufacturer of commercial kitchen, scientific, solar & battery and pharmaceutical equipment.",
    tagline: "Group website for a multi-brand manufacturer and distributor operating in six countries.",
    overview: [
      "US Incorporated is a group that manufactures and distributes commercial kitchen equipment, scientific and medical instruments, solar & battery systems and pharmaceuticals. It operates in six countries — with headquarters in the USA and offices including Hong Kong, Canada, Turkey, South Korea and Uzbekistan — and has expansion projects in Turkey, Congo and Nigeria.",
      "I built the group site while working at Pancode."
    ],
    brief:
      "One site had to represent six sister brands — US Refrigeration, US Cooking, US Stainless, US Solar & Battery, US Scientific and US Medical — each with its own identity, while still reading as one group.",
    approach:
      "I built a React and Redux front end with a persistent brand bar, so every sister company is one click away, and a rotating hero carousel that gives each brand its moment. A companies directory, about section, contact form and warranty-support pages round out the customer-facing side.",
    features: [
      { title: "Multi-brand navigation", text: "A logo bar linking all six sister companies from every page." },
      { title: "Brand hero carousel", text: "Rotating slides, each carrying one brand's message." },
      { title: "Companies directory", text: "A grid of group companies leading into each brand." },
      { title: "Global footprint", text: "An about section covering countries of operation and expansion markets." },
      { title: "Contact & warranty", text: "A contact form plus a dedicated warranty-support area." },
      { title: "Responsive by default", text: "The brand grid reflows into two-column cards on phones." }
    ],
    stack: [
      { name: "React.js", use: "Component-based multi-brand UI" },
      { name: "Redux", use: "Shared state across brands and pages" },
      { name: "Material UI", use: "Form and layout components" },
      { name: "NestJS", use: "Contact and content API" }
    ],
    mobileNote: "On phones the brand bar becomes a tappable grid of company cards, and the contact form goes full-width.",
    gallery: shots("us-incorporated", 2, 3)
  },
  {
    id: "pancode",
    title: "Pancode",
    category: "Corporate",
    link: "https://www.pan-code.com/",
    context: "Pancode",
    role: "Frontend Developer",
    sector: "Software studio",
    market: "Damascus, Syria",
    summary: "Motion-rich corporate site for Pancode — presenting the studio's products and services.",
    tagline: "Corporate website for Pancode, the software studio where I worked as a frontend developer.",
    overview: [
      "Pancode is a digital studio building software, platforms and AI products. As a frontend developer there from December 2022 to September 2023, I built the company's corporate website to present its products and services and help visitors find the right offering."
    ],
    brief:
      "Position a young studio as a credible, modern partner. The site itself had to work as a portfolio piece — proof of the team's frontend craft.",
    approach:
      "I built it with React and Chakra UI, using GSAP for scroll-driven storytelling and reveal animations, and a shared component system across the service and product sections.",
    features: [
      { title: "Scroll-driven storytelling", text: "GSAP-powered reveals and transitions that guide visitors through the studio's story." },
      { title: "Services & products", text: "Clear sections presenting what the studio builds and offers." },
      { title: "Shared component system", text: "Chakra UI components reused across every section for consistency." }
    ],
    stack: [
      { name: "React.js", use: "Site structure and UI" },
      { name: "Chakra UI", use: "Component system and theming" },
      { name: "GSAP", use: "Scroll-driven animation" }
    ],
    note: "pan-code.com has since been redesigned; the screenshot shows the version I built.",
    gallery: shots("pancode", 1, 0)
  },
  {
    id: "marmani",
    title: "Marmani",
    category: "Corporate",
    link: "https://marmani.netlify.app/",
    context: "Client project",
    role: "Frontend Developer",
    sector: "Education · Training",
    market: "United Arab Emirates",
    summary: "Bilingual site for a UAE training institute — audience pathways, programmes and enquiries.",
    tagline: "Bilingual website for a UAE institute for professional training and development.",
    overview: [
      "Marmani Institute for Professional Training and Development is a UAE training provider. Its courses cover law, diplomacy and etiquette, engineering design, Photoshop, HR management, personal skills and technical training such as AI, as well as children's and family programmes."
    ],
    brief:
      "Speak to very different audiences — university students, working professionals, and business owners and executives — in both Arabic and English, and turn interest into course enquiries.",
    approach:
      "I built it with React and Chakra UI, with Arabic and English versions. Content is organised by audience, then vision and mission, a “Why Marmani” value grid and programme cards — brought to life with Swiper carousels, Three.js visual touches and AOS scroll reveals.",
    features: [
      { title: "Arabic & English", text: "Full bilingual content with a language switcher in the header." },
      { title: "Audience pathways", text: "Dedicated messaging for students, job holders and executives." },
      { title: "“Why Marmani” grid", text: "Six value propositions, from simplified learning to continuous student support." },
      { title: "Programme catalogue", text: "Course cards presenting each programme at a glance." },
      { title: "Enquiry form", text: "A get-in-touch form capturing name, email, phone and message." },
      { title: "Responsive layouts", text: "Audience cards and programmes stack cleanly on phones." }
    ],
    stack: [
      { name: "React.js", use: "Site structure and UI" },
      { name: "Chakra UI", use: "Component system and responsive styling" },
      { name: "Three.js", use: "3D visual accents" },
      { name: "AOS", use: "Scroll-reveal animations" },
      { name: "Swiper", use: "Carousels" }
    ],
    mobileNote: "On phones the audience cards stack into a single column and the hero copy stays readable over the imagery.",
    gallery: shots("marmani", 3, 3)
  }
];

export const getProject = (id) => projects.find((project) => project.id === id);
